import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import useAuthContext from './AuthContext';
import useErrorMessage from '../utils/ErrorMessage';
import { useSocketContext } from './SocketContext';

const AssurmaxContext = createContext({});

export const AssurmaxContextProvider = ({ children }) => {
  const { dispatchAPI, user } = useAuthContext();
  const { socket } = useSocketContext();
  const { message } = useErrorMessage();
  const [BROKER_CLAIMS, setComplaintCount] = useState(0);
  const [UNPAID_SUSPENDED, setUnpaidCount] = useState(0);
  const [ticketCount, setTicketCount] = useState({
    PORTFOLIO_AMENDMENTS: 0,
    QUOTES_IN_EXEMPTION: 0
  });
  const [MESSAGES, setMessagesCount] = useState(0);
  const [MISSING_PARTS, setMissingPartsCount] = useState(0);

  const getTicketCount = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/tickets/count${
          user?.permissionManagement?.quotations ? '' : `?user=${user?._id}`
        }`
      });
      setTicketCount(data);
    } catch (e) {
      message(e);
      setTicketCount({
        PORTFOLIO_AMENDMENTS: 0,
        QUOTES_IN_EXEMPTION: 0
      });
    }
  };

  const getMessages = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/messages/count`
      });
      setMessagesCount(data);
    } catch (e) {
      if (e.response) message(e.response.status);
      setMessagesCount(0);
    }
  };

  const getComplaintsCount = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/reclamations/count' });
      setComplaintCount(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getMissingParts = useCallback(async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/contractDocuments/count/extranet${
          user?.permissionManagement?.quotations ? '' : `?user=${user?._id}`
        }`
      });
      setMissingPartsCount(data);
    } catch (e) {
      if (e.response) message(e.response.status);
      setMissingPartsCount(0);
    }
    // eslint-disable-next-line
  }, []);

  const getUnpaid = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: 'contracts/unpaid/count'
      });
      setUnpaidCount(data);
    } catch (e) {
      if (e.response) message(e.response.status);
      setUnpaidCount(0);
    }
  };

  useEffect(() => {
    if (user)
      (async () => {
        await getMessages();
        await getMissingParts();
        await getComplaintsCount();
        if (user.role !== 'guests:GUEST') {
          await getTicketCount();
          await getUnpaid();
        }
      })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      await getMissingParts();
      await getComplaintsCount();
      if (user?.role !== 'guests:GUEST') {
        await getTicketCount();
      }
    }, 180000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('newMessage', () => {
        getMessages();
      });
    }
    // eslint-disable-next-line
  }, [socket]);

  return (
    <AssurmaxContext.Provider
      value={{
        PORTFOLIO_AMENDMENTS_IN_EXEMPTION:
          ticketCount?.PORTFOLIO_AMENDMENTS || 0,
        QUOTES_IN_EXEMPTION: ticketCount?.QUOTES_IN_EXEMPTION || 0,
        EXEMPTION_AWAITING_INFORMATION:
          ticketCount?.EXEMPTION_AWAITING_INFORMATION || 0,
        MESSAGES,
        MISSING_PARTS,
        BROKER_CLAIMS,
        UNPAID_SUSPENDED,
        getMessages
      }}
    >
      {children}
    </AssurmaxContext.Provider>
  );
};

AssurmaxContextProvider.propTypes = {
  children: PropTypes.element.isRequired
};

export default () => useContext(AssurmaxContext);
